import { useEffect, useState } from 'react';

/* ── Anneau de score segmenté ────────────────────────────────────────
   La signature graphique de la charte : quatre arcs pastel (jaune, rose,
   bleu, kaki) séparés par des respirations, qui se remplissent au fur et
   à mesure du score. Les arcs non atteints restent en version pâle — on
   garde donc le nuancier complet à l'écran quel que soit le résultat.

   Utilisé pour le score de conformité (ResultsDisplay) ; le loader
   d'analyse (LoaderRing, plus bas) reprend exactement la même géométrie. */

const SEGMENTS = [
  { fill: '#f5dd6d', pale: '#fbefc0' }, // jaune
  { fill: '#f2a0ce', pale: '#fbe2f0' }, // rose
  { fill: '#b7cdf0', pale: '#e3ecfa' }, // bleu
  { fill: '#a9be7e', pale: '#e8eed6' }, // kaki
];

const R = 78;
const CIRC = 2 * Math.PI * R;
const GAP = 16;
const SEG_SPAN = CIRC / SEGMENTS.length;
const SEG_LEN = SEG_SPAN - GAP;

interface Props {
  /** Score 0–100. */
  score: number;
  /** Diamètre rendu en px. */
  size?: number;
  /** Contenu central. Par défaut : le score + « /100 ». */
  children?: React.ReactNode;
  /** Épaisseur du tracé (unités du viewBox 200×200). */
  thickness?: number;
  className?: string;
}

export default function ScoreRing({
  score,
  size = 176,
  thickness = 15,
  children,
  className = '',
}: Props) {
  // Remplissage animé au montage (0 → score).
  const [drawn, setDrawn] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(Math.max(0, Math.min(100, score))));
    return () => cancelAnimationFrame(id);
  }, [score]);

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 200 200" className="-rotate-90">
        {SEGMENTS.map((seg, i) => {
          const start = i * SEG_SPAN + GAP / 2;
          // Part du segment couverte par le score (0 → 1).
          const ratio = Math.max(0, Math.min(1, (drawn - i * 25) / 25));
          return (
            <g key={i}>
              {/* Piste pâle — toujours visible, c'est elle qui porte le nuancier */}
              <circle
                cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
                stroke={seg.pale} strokeWidth={thickness}
                strokeDasharray={`${SEG_LEN} ${CIRC - SEG_LEN}`}
                strokeDashoffset={-start}
              />
              {/* Arc rempli */}
              <circle
                cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
                stroke={seg.fill} strokeWidth={thickness}
                strokeDasharray={`${SEG_LEN * ratio} ${CIRC - SEG_LEN * ratio}`}
                strokeDashoffset={-start}
                style={{
                  transition: `stroke-dasharray 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 110}ms`,
                }}
              />
            </g>
          );
        })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children ?? (
          <>
            <span className="font-display text-slate-900 leading-none" style={{ fontSize: size * 0.28 }}>
              {score}
            </span>
            <span className="text-slate-400 mt-1" style={{ fontSize: size * 0.075 }}>
              /100
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Anneau du loader ───────────────────────────────────────────────
   Le préloader reprend littéralement le motif du scoring : même rayon,
   mêmes quatre arcs pastel, mêmes pistes pâles. Deux régimes :

   - progress === null (aucun event SSE encore reçu) : les arcs pleins
     s'allument à tour de rôle sur les pistes pâles — une vague de couleur
     qui fait le tour de l'anneau. Rien ne tourne : la géométrie reste
     rigoureusement celle du score final, pour que la bascule vers la
     progression réelle ne saute pas.
   - progress connu : les arcs se remplissent comme sur le score final
     (mêmes transitions), le pourcentage prend la place du score au centre.

   L'arc en cours de remplissage garde une légère pulsation : on voit que
   l'IA cogite même quand la valeur stagne (l'arbitrage dure 1-2 min). */
export function LoaderRing({
  progress,
  size = 160,
  thickness = 15,
  className = '',
  children,
}: {
  /** 0–100, ou null tant que le backend n'a rien émis. */
  progress: number | null;
  size?: number;
  thickness?: number;
  className?: string;
  /** Contenu central. Par défaut : le pourcentage, ou trois points en attente. */
  children?: React.ReactNode;
}) {
  const indeterminate = progress === null;
  const value = indeterminate ? 0 : Math.max(0, Math.min(100, progress));
  // Indice de l'arc en cours de remplissage (celui qui pulse).
  const activeIdx = Math.min(SEGMENTS.length - 1, Math.floor(value / 25));

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 200 200" className="-rotate-90">
        {SEGMENTS.map((seg, i) => {
          const start = i * SEG_SPAN + GAP / 2;
          const ratio = indeterminate ? 1 : Math.max(0, Math.min(1, (value - i * 25) / 25));
          const isActive = !indeterminate && i === activeIdx && ratio < 1;
          return (
            <g key={i}>
              <circle
                cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
                stroke={seg.pale} strokeWidth={thickness}
                strokeDasharray={`${SEG_LEN} ${CIRC - SEG_LEN}`}
                strokeDashoffset={-start}
              />
              <circle
                cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
                stroke={seg.fill} strokeWidth={thickness}
                strokeDasharray={`${SEG_LEN * ratio} ${CIRC - SEG_LEN * ratio}`}
                strokeDashoffset={-start}
                className={indeterminate ? 'loader-ring-wave' : isActive ? 'loader-ring-active' : undefined}
                style={{
                  // La vague décale chaque arc d'un quart de période : le sens
                  // de parcours est celui du remplissage (jaune → kaki).
                  animationDelay: indeterminate ? `${i * 0.55}s` : undefined,
                  transition: indeterminate
                    ? 'none'
                    : 'stroke-dasharray 700ms cubic-bezier(0.22, 1, 0.36, 1), opacity 400ms ease-out',
                }}
              />
            </g>
          );
        })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children ?? (indeterminate ? (
          /* Trois points qui reprennent la cadence de la vague */
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="loader-ring-dot h-1.5 w-1.5 rounded-full bg-slate-400"
                style={{ animationDelay: `${i * 0.18}s` }}
              />
            ))}
          </div>
        ) : (
          /* « 57% » d'un seul tenant, en Acorn, centré dans l'anneau */
          <span className="font-display text-slate-900 leading-none tabular-nums" style={{ fontSize: size * 0.22 }}>
            {Math.round(value)}%
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Variante indéterminée compacte ─────────────────────────────────
   Pour les petits états d'attente (boutons, cartes) : les quatre arcs
   pastel tournent lentement. */
export function PulseRing({ size = 88, thickness = 13 }: { size?: number; thickness?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" className="animate-spin-slow">
      {SEGMENTS.map((seg, i) => {
        const start = i * SEG_SPAN + GAP / 2;
        return (
          <circle
            key={i}
            cx="100" cy="100" r={R} fill="none" strokeLinecap="round"
            stroke={seg.fill} strokeWidth={thickness}
            strokeDasharray={`${SEG_LEN} ${CIRC - SEG_LEN}`}
            strokeDashoffset={-start}
            opacity={0.45 + i * 0.18}
          />
        );
      })}
    </svg>
  );
}
